/*
 * This file is part of the Akershus Eiendom Nettsted 2019 application.
 *
 * (c) APT AS
 *
 * For the full copyright and license information, please view the LICENSE
 * file that was distributed with this source code.
 */

import React from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import './Arrow.scss';

/**
 * This is the Arrow component.
 *
 * @author Thomas Sømoen <thomas@apt.no>
 *
 * @return {JSX}
 */

function Arrow({ className, long, up, down, right, left, title }) {
  function renderSvg() {
    if (long) {
      return (
        <svg
          width="18"
          height="12"
          viewBox="0 0 18 12"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <title>{title}</title>
          <path
            fillRule="evenodd"
            clipRule="evenodd"
            d="M3.8147e-06 5.99981C3.8147e-06 5.83405 0.0658518 5.67508 0.183062 5.55787C0.300272 5.44066 0.459244 5.37481 0.625004 5.37481L15.3663 5.37481L11.4325 1.44231C11.3151 1.32495 11.2492 1.16578 11.2492 0.999813C11.2492 0.833843 11.3151 0.674671 11.4325 0.557313C11.5499 0.439954 11.709 0.374023 11.875 0.374023C12.041 0.374023 12.2001 0.439955 12.3175 0.557313L17.3175 5.55731C17.3757 5.61537 17.4219 5.68434 17.4534 5.76027C17.4849 5.8362 17.5011 5.9176 17.5011 5.99981C17.5011 6.08202 17.4849 6.16342 17.4534 6.23935C17.4219 6.31529 17.3757 6.38426 17.3175 6.44231L12.3175 11.4423C12.2001 11.5597 12.041 11.6256 11.875 11.6256C11.709 11.6256 11.5499 11.5597 11.4325 11.4423C11.3151 11.325 11.2492 11.1658 11.2492 10.9998C11.2492 10.8338 11.3151 10.6747 11.4325 10.5573L15.3663 6.62481L0.625004 6.62481C0.459244 6.62481 0.300272 6.55897 0.183062 6.44176C0.0658518 6.32455 3.8147e-06 6.16557 3.8147e-06 5.99981Z"
            fill="currentColor"
          />
        </svg>
      );
    }

    if (up) {
      return (
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="18"
          height="25"
          viewBox="0 0 18 25"
        >
          <title>{title}</title>
          <g transform="translate(-23 -2)">
            <path className="a" d="M32,3V26" />
            <path className="a" d="M40,12,32,3l-8,9" />
          </g>
        </svg>
      );
    }

    if (down) {
      return (
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="18"
          height="25"
          viewBox="0 0 18 25"
        >
          <title>{title}</title>
          <g transform="translate(41 27) rotate(180)">
            <path className="a" d="M32,3V26" />
            <path className="a" d="M40,12,32,3l-8,9" />
          </g>
        </svg>
      );
    }

    if (left || right) {
      return (
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="25"
          height="18"
          viewBox="0 0 25 18"
        >
          <title>{title}</title>
          <g transform="translate(-2 41) rotate(-90)">
            <path className="a" d="M32,3V26" />
            <path className="a" d="M40,12,32,3l-8,9" />
          </g>
        </svg>
      );
    }

    return null;
  }

  return (
    <span
      className={classnames(
        'svg',
        'svg-arrow',
        {
          'svg-arrow-up': up,
          'svg-arrow-down': down,
          'svg-arrow-right': right,
          'svg-arrow-left': left,
          'svg-arrow-long': long,
        },
        className
      )}
    >
      <span>{renderSvg()}</span>
    </span>
  );
}

/**
 * Declare expected prop types.
 *
 * @type {Object}
 */
Arrow.propTypes = {
  className: PropTypes.string,
  up: PropTypes.bool,
  down: PropTypes.bool,
  right: PropTypes.bool,
  left: PropTypes.bool,
  long: PropTypes.bool,
  title: PropTypes.string,
};

/**
 * Declare defaults for non-required props.
 *
 * @type {Object}
 */
Arrow.defaultProps = {
  up: false,
  down: false,
  right: false,
  left: false,
  long: false,
  title: 'Arrow',
};

export default Arrow;
